// Considered: MultiSelectComponent (atoms/multi-select) — rejected because it is a
// specialized avatar/object-value picker: hardcoded optionLabel="name", its own
// item/selected templates, internal selection state and an empty-state overlay,
// and no optionValue or itemTemplate passthrough. Consumers that select plain
// string values with a custom item template (e.g. the context use-case picker)
// cannot use it without lossy object mapping. This atom is the thin, general
// design-system boundary over PrimeReact MultiSelect.
import React, { useId } from 'react';
import { useTranslation } from 'localization';
// eslint-disable-next-line no-restricted-imports -- atoms are the layer that wraps PrimeReact
import { MultiSelect, type MultiSelectProps } from 'primereact/multiselect';
import { nameAttrsFor } from '../Dropdown/dropdownAccessibility';
import './MultiSelectAtom.scss';

export type MultiSelectAtomProps = MultiSelectProps;

type PtPart = object | ((options: unknown) => object) | undefined;
const withAttrs = (own: PtPart, attrs: object): PtPart =>
  typeof own === 'function' ? (options: unknown) => ({ ...attrs, ...own(options) }) : { ...attrs, ...own };

/**
 * Thin design-system wrapper over PrimeReact MultiSelect.
 *
 * Guarantees `value` is always an array: PrimeReact's selection helpers
 * (`isSelected` / `isAllSelected`) call `.some` on it, so a non-array value
 * crashes the panel. Coercing here keeps every consumer safe.
 *
 * Also completes the accessibility PrimeReact (10.9) leaves unfinished, in one place for
 * every multiselect:
 * - the keyboard combobox and its listbox take the caller's name (`aria-label` /
 *   `aria-labelledby`, `placeholder` as the fallback) — PrimeReact names neither;
 * - the open combobox points at its listbox (`aria-controls`, required for an expanded
 *   combobox); set from PrimeReact's own `overlayVisible` state — the same state that drives
 *   `aria-expanded` and mounts the listbox — so the two never disagree, not even during the
 *   panel's enter transition;
 * - the panel's filter field is named;
 * - each option's checkbox is decorative — the option's `aria-selected` already carries
 *   the state — so it is hidden rather than left as a second, unnamed toggle inside a
 *   toggle (axe `nested-interactive`, `aria-toggle-field-name`, `label`);
 * - the "select all" checkbox is a real control, so it keeps its input and gets a name;
 * - the "no results" row inside the listbox is a disabled option, since a listbox may
 *   only contain options;
 * - the scrolling options wrapper is a tab stop, so a keyboard user can scroll it.
 *
 * The panel carries `multi-select-atom__panel`, which themes it for dark mode
 * (PrimeReact's panel styles are light-only).
 */
const MultiSelectAtom: React.FC<MultiSelectAtomProps> = ({ value, className, panelClassName, ...rest }) => {
  const { t } = useTranslation();
  const safeValue = Array.isArray(value) ? value : [];
  const listId = `${useId()}-list`;

  const labelledBy = rest['aria-labelledby'] ?? rest.ariaLabelledBy ?? undefined;
  const name = labelledBy ? undefined : rest['aria-label'] ?? rest.placeholder ?? undefined;
  const nameAttrs = nameAttrsFor(labelledBy, name);

  const callerPt = (rest.pt ?? {}) as Record<string, PtPart>;
  const callerFilterInput = (callerPt.filterInput ?? {}) as Record<string, PtPart>;
  const callerCheckbox = (callerPt.checkbox ?? {}) as Record<string, PtPart>;
  const callerHeaderCheckbox = (callerPt.headerCheckbox ?? {}) as Record<string, PtPart>;
  const pt = {
    ...callerPt,
    input: (options: unknown) => {
      const open = (options as { state?: { overlayVisible?: boolean } } | undefined)?.state?.overlayVisible === true;
      const own = callerPt.input;
      const ownAttrs = typeof own === 'function' ? own(options) : own ?? {};
      return { ...nameAttrs, ...(open ? { 'aria-controls': listId } : {}), ...ownAttrs };
    },
    list: withAttrs(callerPt.list, { id: listId, ...nameAttrs }),
    wrapper: withAttrs(callerPt.wrapper, { tabIndex: 0 }),
    emptyMessage: withAttrs(callerPt.emptyMessage, { role: 'option', 'aria-disabled': true, 'aria-selected': false }),
    filterInput: { ...callerFilterInput, root: withAttrs(callerFilterInput.root, { 'aria-label': t('multiSelect.searchOptions') }) },
    checkbox: {
      ...callerCheckbox,
      // `inert`, not just `aria-hidden` + `tabIndex=-1`: a negative tabindex still leaves the
      // input focusable by assistive tech inside the option (axe `nested-interactive`).
      root: withAttrs(callerCheckbox.root, { role: undefined, 'aria-checked': undefined, 'aria-hidden': true, inert: '' }),
      input: withAttrs(callerCheckbox.input, { tabIndex: -1 }),
    },
    // "Select all" IS a control: its input keeps the role and gets a name; only the
    // root's duplicate `role="checkbox"` goes.
    headerCheckbox: {
      ...callerHeaderCheckbox,
      root: withAttrs(callerHeaderCheckbox.root, { role: undefined, 'aria-checked': undefined }),
      input: withAttrs(callerHeaderCheckbox.input, { 'aria-label': t('multiSelect.selectAll') }),
    },
  };

  return (
    <MultiSelect
      {...rest}
      value={safeValue}
      className={`multi-select-atom ${className ?? ''}`.trim()}
      panelClassName={`multi-select-atom__panel ${panelClassName ?? ''}`.trim()}
      {...nameAttrs}
      pt={pt as MultiSelectProps['pt']}
    />
  );
};

export default MultiSelectAtom;
